<template>
  <div class="grace-periods-page" data-testid="grace-periods-page">
    <page-header
      :title="t('Períodos de Carência')"
      icon="pi pi-clock"
    />

    <div class="toolbar" data-testid="grace-periods-toolbar">
      <Button
        :label="t('Conceder carência')"
        icon="pi pi-plus"
        severity="primary"
        data-testid="grace-periods-grant-btn"
        @click="openGrantModal"
      />
      <SelectButton
        v-model="viewMode"
        :options="viewOptions"
        option-label="label"
        option-value="value"
        :allow-empty="false"
        data-testid="grace-periods-view-toggle"
      />
    </div>

    <GracePeriodsTable
      :rows="displayRows"
      @revoke="openRevokeModal"
      @refresh="handleRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useGraceAdmin } from '../../../composables/useGraceAdmin'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import PageHeader from '../../../ui/PageHeader.vue'
import SelectButton from '../../../ui/SelectButton.vue'
import GracePeriodsTable from './GracePeriodsTable.vue'
import type { Grace_PeriodRow } from '../../../contracts/Grace_PeriodRow'

const { t } = useI18n()
const { graceRows, listGrace } = useGraceAdmin()
const { formatDate, fromApiDateToDate } = useDateFormatter()

type ViewMode = 'live' | 'all'

const viewMode = ref<ViewMode>('live')

const viewOptions = [
  { label: t('Vigentes'), value: 'live' },
  { label: t('Histórico'), value: 'all' },
]

const displayRows = computed(() => {
  let result = graceRows.value

  if (viewMode.value === 'live') {
    result = result.filter((r) => r.is_live === true)
  }

  return result.map((r) => ({
    ...r,
    starts_at_display: r.starts_at ? formatDate(fromApiDateToDate(r.starts_at)) : '',
    ends_at_display: r.ends_at ? formatDate(fromApiDateToDate(r.ends_at)) : '',
  }))
})

function openGrantModal(): void {
  eventBus.emit('modal.graceGrant.open')
}

function openRevokeModal(row: Grace_PeriodRow): void {
  eventBus.emit('modal.graceRevoke.open', { row })
}

async function handleRefresh(): Promise<void> {
  try {
    await listGrace()
  } catch (err) {
    log('GracePeriodsPage', 'handleRefresh', 'Erro ao recarregar períodos de carência', err)
  }
}

onMounted(async () => {
  try {
    await listGrace()
  } catch (err) {
    log('GracePeriodsPage', 'onMounted', 'Erro ao carregar períodos de carência', err)
  }
  eventBus.on('grace.refresh', handleRefresh)
  eventBus.on('graceGrant.saved', handleRefresh)
  eventBus.on('graceRevoke.saved', handleRefresh)
})

onBeforeUnmount(() => {
  eventBus.off('grace.refresh', handleRefresh)
  eventBus.off('graceGrant.saved', handleRefresh)
  eventBus.off('graceRevoke.saved', handleRefresh)
})
</script>

<style scoped>
.grace-periods-page {
  padding: 24px;
}

.toolbar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
}
</style>
